const roundAngle = value => Math.round((value + Number.EPSILON) * 1e6) / 1e6;
const roundCoord = value => Math.round((value + Number.EPSILON) * 1e4) / 1e4;

const polarPoint = (cx, cy, radius, angleDeg) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: roundCoord(cx + radius * Math.cos(rad)),
    y: roundCoord(cy + radius * Math.sin(rad))
  };
};

/**
 * Angular span of each slice, proportional to its value, laid out clockwise
 * starting at 0° (3 o'clock / East) — matching react-minimal-pie-chart's defaults.
 * @param {number[]} values
 * @returns {{ startAngle: number, endAngle: number }[]}
 */
export const sliceAngles = values => {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return values.map(() => ({ startAngle: 0, endAngle: 0 }));

  let cursor = 0;
  return values.map(value => {
    const startAngle = roundAngle(cursor);
    cursor += (value / total) * 360;
    return { startAngle, endAngle: roundAngle(cursor) };
  });
};

/**
 * Solid pie wedge path from the center to an arc between two angles (clockwise).
 */
export const describeWedge = (cx, cy, radius, startAngle, endAngle) => {
  const start = polarPoint(cx, cy, radius, startAngle);
  const end = polarPoint(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
};

const describeFullCircle = (cx, cy, radius) =>
  `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} ` +
  `A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`;

/**
 * Build the fillable SVG paths for a solid pie, one per data entry.
 * @param {{ value: number, color: string, key?: string|number }[]} entries
 * @param {{ cx: number, cy: number, radius: number }} geometry
 * @returns {{ path: string, color: string, key: string|number }[]}
 */
export const buildPieSlices = (entries, { cx, cy, radius }) => {
  if (entries.length === 0) return [];

  if (entries.length === 1) {
    const [entry] = entries;
    return [{ path: describeFullCircle(cx, cy, radius), color: entry.color, key: entry.key ?? 0 }];
  }

  const angles = sliceAngles(entries.map(entry => entry.value));
  return entries.map((entry, index) => ({
    path: describeWedge(cx, cy, radius, angles[index].startAngle, angles[index].endAngle),
    color: entry.color,
    key: entry.key ?? index
  }));
};
